/**
 * Zoom Integration Utilities
 * Handles Zoom OAuth flow and meeting creation
 */

// Zoom OAuth configuration
const ZOOM_CONFIG = {
  clientId: process.env.REACT_APP_ZOOM_CLIENT_ID || 'your_zoom_client_id',
  // No clientSecret in the browser — the Zoom token exchange happens server-side
  // (POST /api/zoom/token), which holds the real secret.
  redirectUri: process.env.REACT_APP_ZOOM_REDIRECT_URI || 'https://app.aideyapp.com/api/auth/zoom/callback',
  baseUrl: 'https://api.zoom.us/v2',
  authUrl: 'https://zoom.us/oauth/authorize',
  tokenUrl: 'https://zoom.us/oauth/token'
};

/**
 * Check if user has a valid Zoom access token
 */
export function isZoomConnected() {
  const token = localStorage.getItem('zoom_access_token');
  const expiry = localStorage.getItem('zoom_token_expiry');
  
  if (!token || !expiry) return false;
  
  // Check if token is expired (with 5 minute buffer)
  const expiryTime = new Date(expiry);
  const now = new Date();
  const buffer = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  return now < (expiryTime.getTime() - buffer);
}

/**
 * Get stored Zoom access token
 */
export function getZoomAccessToken() {
  if (!isZoomConnected()) return null;
  return localStorage.getItem('zoom_access_token');
}

/**
 * Initiate Zoom OAuth flow
 */
export function initiateZoomAuth() {
  const scope = 'meeting:write user:read';
  const state = generateRandomState();
  
  localStorage.setItem('zoom_oauth_state', state);
  
  const authUrl = `${ZOOM_CONFIG.authUrl}?` + 
    `response_type=code&` +
    `client_id=${ZOOM_CONFIG.clientId}&` +
    `redirect_uri=${encodeURIComponent(ZOOM_CONFIG.redirectUri)}&` +
    `scope=${encodeURIComponent(scope)}&` +
    `state=${state}`;
  
  console.log('Zoom Auth URL:', authUrl);
  window.location.href = authUrl;
}

/**
 * Handle OAuth callback and exchange code for tokens
 */
export async function handleZoomCallback(code, state) {
  // Verify state to prevent CSRF attacks
  const storedState = localStorage.getItem('zoom_oauth_state');
  if (state !== storedState) {
    throw new Error('Invalid OAuth state parameter');
  }
  
  try {
    const tokenData = await exchangeCodeForTokens(code);
    
    // Store tokens securely
    localStorage.setItem('zoom_access_token', tokenData.access_token);
    localStorage.setItem('zoom_refresh_token', tokenData.refresh_token);
    
    // Calculate expiry time
    const expiryTime = new Date(Date.now() + (tokenData.expires_in * 1000));
    localStorage.setItem('zoom_token_expiry', expiryTime.toISOString());
    
    // Clean up
    localStorage.removeItem('zoom_oauth_state');
    
    return true;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
}

/**
 * Exchange authorization code for access tokens
 */
async function exchangeCodeForTokens(code) {
  const response = await fetch('/api/zoom/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri: ZOOM_CONFIG.redirectUri
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to exchange code for tokens');
  }
  
  return response.json();
}

/**
 * Create a Zoom meeting
 * @deprecated This path reads a Zoom token from localStorage which is never
 * populated by the Settings → Integrations OAuth flow.  All Aidey Chat meeting
 * creation now goes through createZoomMeetingServer (Aidey Backend/lib/videoMeetings.js)
 * which reads the encrypted token from the VideoConnection DB table.
 * This function is retained only for the legacy handleAddTaskFromChat path.
 */
export async function createZoomMeeting(meetingDetails) {
  const accessToken = getZoomAccessToken();
  if (!accessToken) {
    throw new Error('No valid Zoom access token. Please connect to Zoom first.');
  }
  
  try {
    const response = await fetch('/api/zoom/meetings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        topic: meetingDetails.title || 'Aidey Meeting',
        type: meetingDetails.startTime ? 2 : 1, // 1 = instant, 2 = scheduled
        start_time: meetingDetails.startTime, // ISO 8601 format
        duration: meetingDetails.duration || 60, // minutes
        timezone: meetingDetails.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        settings: {
          join_before_host: true,
          mute_upon_entry: false,
          use_pmi: false,
          approval_type: 0, // 0 = automatically approve
          audio: 'both', // telephony and voip
          auto_recording: 'none'
        }
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create Zoom meeting');
    }
    
    const meeting = await response.json();
    return {
      joinUrl: meeting.join_url,
      meetingId: meeting.id,
      password: meeting.password,
      hostUrl: meeting.start_url
    };
  } catch (error) {
    console.error('Error creating Zoom meeting:', error);
    throw error;
  }
}

/**
 * Refresh access token using refresh token
 */
export async function refreshZoomToken() {
  const refreshToken = localStorage.getItem('zoom_refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  
  try {
    const response = await fetch('/api/zoom/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }
    
    const tokenData = await response.json();
    
    // Update stored tokens
    localStorage.setItem('zoom_access_token', tokenData.access_token);
    if (tokenData.refresh_token) {
      localStorage.setItem('zoom_refresh_token', tokenData.refresh_token);
    }
    
    // Update expiry
    const expiryTime = new Date(Date.now() + (tokenData.expires_in * 1000));
    localStorage.setItem('zoom_token_expiry', expiryTime.toISOString());
    
    return tokenData.access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Clear invalid tokens
    clearZoomTokens();
    throw error;
  }
}

/**
 * Clear all stored Zoom tokens
 */
export function clearZoomTokens() {
  localStorage.removeItem('zoom_access_token');
  localStorage.removeItem('zoom_refresh_token');
  localStorage.removeItem('zoom_token_expiry');
  localStorage.removeItem('zoom_oauth_state');
}

/**
 * Disconnect from Zoom
 */
export function disconnectZoom() {
  clearZoomTokens();
  // You might want to also revoke the token on Zoom's side
}

/**
 * Get Zoom user information
 */
export async function getZoomUserInfo() {
  const accessToken = getZoomAccessToken();
  if (!accessToken) {
    throw new Error('No Zoom access token available');
  }

  try {
    const response = await fetch('/api/zoom/user', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get user info');
    }

    const userInfo = await response.json();
    return userInfo;
  } catch (error) {
    console.error('Error getting Zoom user info:', error);
    throw error;
  }
}

/**
 * Generate random state for OAuth security
 */
function generateRandomState() {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Format meeting details for Zoom API
 */
export function formatMeetingForZoom(taskDetails) {
  const startTime = taskDetails.date && taskDetails.time 
    ? `${taskDetails.date}T${taskDetails.time}:00`
    : null;
  
  return {
    title: taskDetails.title,
    startTime: startTime,
    duration: taskDetails.duration || 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}
